import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import PremiumFeatureGate from '../premium/PremiumFeatureGate';
import PremiumBadge from '../premium/PremiumBadge';
import PremiumLockedSection from '../premium/PremiumLockedSection';
import usePremiumEntitlements from '../../hooks/usePremiumEntitlements';
import MaintenanceReceiptUpload from './MaintenanceReceiptUpload';
import FuelTrackingPanel from './FuelTrackingPanel';

type MaintenanceItem = {
  id: number;
  title: string;
  category: string;
  service_date: string;
  mileage: number | null;
  document_count?: number;
  receipt_path?: string | null;
  receipt_mime?: string | null;
};

type UpcomingItem = {
  id: number;
  title: string;
  status: string;
  miles_remaining: number | null;
  next_due_date: string | null;
};

type Props = {
  vehicleId: number;
};

const statusLabel: Record<string, string> = {
  overdue: 'Overdue',
  due: 'Due',
  due_soon: 'Due soon',
  ok: 'On track',
};

/**
 * Manage Build → Maintenance panel.
 * Free: history + basic schedules. Premium: advanced combined intervals.
 */
const MaintenancePanel: React.FC<Props> = ({ vehicleId }) => {
  const { hasCapability } = usePremiumEntitlements();
  const hasPremiumAi = hasCapability('premium_ai');
  const [items, setItems] = useState<MaintenanceItem[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingItem[]>([]);
  const [mileage, setMileage] = useState<number | null>(null);
  const [visibility, setVisibility] = useState('private');
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [serviceDate, setServiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [entryMileage, setEntryMileage] = useState('');
  const [saving, setSaving] = useState(false);
  const [scheduleGate, setScheduleGate] = useState(false);
  const [aiGate, setAiGate] = useState(false);
  const [reportsGate, setReportsGate] = useState(false);
  const [scheduleMiles, setScheduleMiles] = useState('5000');
  const [scheduleMonths, setScheduleMonths] = useState('');
  const [nlText, setNlText] = useState('');
  const [nlBusy, setNlBusy] = useState(false);
  const [recordBusy, setRecordBusy] = useState(false);
  const [pendingReceiptId, setPendingReceiptId] = useState<number | null>(null);
  const [expandedReceiptId, setExpandedReceiptId] = useState<number | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await apiService.getMaintenance(vehicleId);
      setItems((data.items as MaintenanceItem[]) ?? []);
      setUpcoming((data.upcoming as UpcomingItem[]) ?? []);
      setMileage((data.vehicle?.current_mileage as number | null | undefined) ?? null);
      setVisibility(String(data.vehicle?.maintenance_visibility ?? 'private'));
      setIsPremium(Boolean(data.is_premium));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load maintenance.');
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [vehicleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await apiService.postMaintenance({
        action: 'create',
        vehicle_id: vehicleId,
        title: title.trim(),
        category: 'general_service',
        service_date: serviceDate,
        mileage: entryMileage ? Number(entryMileage) : null,
      });
      const recordId = (created as { data?: { id?: number } }).data?.id;
      setTitle('');
      setEntryMileage('');
      if (typeof recordId === 'number' && recordId > 0) {
        setPendingReceiptId(recordId);
        setExpandedReceiptId(recordId);
      }
      await load({ silent: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const refreshAfterReceipt = async () => {
    setPendingReceiptId(null);
    await load({ silent: true });
  };

  const setVis = async (next: string) => {
    try {
      await apiService.postMaintenance({ action: 'set_visibility', vehicle_id: vehicleId, visibility: next });
      setVisibility(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not update visibility.');
    }
  };

  const createSchedule = async () => {
    setScheduleGate(false);
    try {
      const body: Record<string, unknown> = {
        action: 'create_schedule',
        vehicle_id: vehicleId,
        title: 'Oil and filter',
        category: 'oil_and_filter',
      };
      if (scheduleMiles) body.interval_miles = Number(scheduleMiles);
      if (scheduleMonths) {
        body.interval_months = Number(scheduleMonths);
        body.whichever_first = true;
      }
      await apiService.postMaintenance(body);
      await load();
    } catch (err: unknown) {
      const coded = err as Error & { code?: string };
      if (coded.code === 'premium_required' || (err instanceof Error && err.message.toLowerCase().includes('premium'))) {
        setScheduleGate(true);
      } else {
        setError(err instanceof Error ? err.message : 'Could not create schedule.');
      }
    }
  };

  const parseNl = async () => {
    if (!nlText.trim()) return;
    setNlBusy(true);
    setError(null);
    try {
      const data = await apiService.postMaintenance({ action: 'parse_nl', text: nlText.trim() });
      const draft = (data as { draft?: Record<string, unknown> }).draft ?? data;
      if (draft.title) setTitle(String(draft.title));
      if (draft.service_date) setServiceDate(String(draft.service_date));
      if (draft.mileage != null) setEntryMileage(String(draft.mileage));
      setNlText('');
    } catch (err: unknown) {
      const coded = err as Error & { code?: string };
      if (coded.code === 'premium_required') {
        setAiGate(true);
        setError('Natural-language entry is included with Bilt:Spec Premium.');
      } else {
        setError(err instanceof Error ? err.message : 'Could not parse description.');
      }
    } finally {
      setNlBusy(false);
    }
  };

  const downloadVehicleRecord = async () => {
    setRecordBusy(true);
    setError(null);
    try {
      const record = await apiService.getMaintenanceVehicleRecord(vehicleId);
      const blob = new Blob([JSON.stringify(record, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bilt-vehicle-record-${vehicleId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      // Printable window for PDF/print workflows
      const w = window.open('', '_blank', 'noopener,noreferrer');
      if (w) {
        const v = (record.vehicle as Record<string, unknown>) || {};
        const items = (record.maintenance_records as Array<Record<string, unknown>>) || [];
        w.document.write(
          `<!doctype html><html><head><title>Bilt:Spec Vehicle Record</title>
          <style>body{font-family:system-ui,sans-serif;padding:24px;color:#111}h1{font-size:20px}table{width:100%;border-collapse:collapse;margin-top:16px}td,th{border-bottom:1px solid #ddd;padding:8px;text-align:left;font-size:13px}</style>
          </head><body>
          <h1>Bilt:Spec Vehicle Record</h1>
          <p>${String(v.display_name || '')} · generated ${String(record.generated_at || '')}</p>
          <table><thead><tr><th>Date</th><th>Service</th><th>Category</th><th>Mileage</th></tr></thead><tbody>
          ${items
            .map(
              (r) =>
                `<tr><td>${String(r.service_date || '')}</td><td>${String(r.title || '')}</td><td>${String(r.category || '')}</td><td>${r.mileage != null ? String(r.mileage) : ''}</td></tr>`
            )
            .join('')}
          </tbody></table>
          <p style="margin-top:24px;font-size:12px;color:#666">Every car has a story.</p>
          <script>window.print()</script>
          </body></html>`
        );
        w.document.close();
      }
    } catch (err: unknown) {
      const coded = err as Error & { code?: string };
      if (coded.code === 'premium_required') {
        setReportsGate(true);
        setError('Bilt:Spec Vehicle Record is included with Bilt:Spec Premium.');
      } else {
        setError(err instanceof Error ? err.message : 'Could not build Vehicle Record.');
      }
    } finally {
      setRecordBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" role="status" aria-busy="true">
        <span className="sr-only">Loading maintenance…</span>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 rounded-xl bg-[#141414]/70 animate-pulse motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div role="alert" className="alert-error">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-500">Vehicle mileage</p>
          <p className="text-2xl font-semibold text-white tabular-nums">
            {mileage != null ? mileage.toLocaleString() : '—'} <span className="text-sm text-zinc-500">mi</span>
          </p>
        </div>
        <label className="text-sm text-zinc-400">
          Public visibility
          <select
            className="ml-2 rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-zinc-200"
            value={visibility}
            onChange={(e) => void setVis(e.target.value)}
          >
            <option value="private">Private</option>
            <option value="public_summary">Public summary</option>
            <option value="public_records">Public records</option>
          </select>
        </label>
      </div>

      {upcoming.filter((u) => u.status !== 'ok').length > 0 ? (
        <section aria-labelledby="maint-due-heading">
          <h3 id="maint-due-heading" className="text-sm font-semibold text-white mb-2">
            Due soon
          </h3>
          <ul className="space-y-2">
            {upcoming
              .filter((u) => u.status !== 'ok')
              .map((u) => (
                <li
                  key={u.id}
                  className="rounded-xl border border-white/10 bg-black/30 px-4 py-3 flex justify-between gap-3"
                >
                  <div>
                    <p className="text-sm text-gray-100">{u.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {statusLabel[u.status] ?? u.status}
                      {u.miles_remaining != null && u.miles_remaining > 0
                        ? ` · ${u.miles_remaining.toLocaleString()} mi remaining`
                        : ''}
                    </p>
                  </div>
                  <span
                    className={`text-xs shrink-0 ${
                      u.status === 'overdue' ? 'text-red-300' : 'text-orange-300'
                    }`}
                  >
                    {statusLabel[u.status]}
                  </span>
                </li>
              ))}
          </ul>
        </section>
      ) : null}

      <FuelTrackingPanel vehicleId={vehicleId} />

      <section aria-labelledby="maint-recent-heading">
        <h3 id="maint-recent-heading" className="text-sm font-semibold text-white mb-2">
          Recent
        </h3>
        {items.length === 0 ? (
          <EmptyState
            variant="dense"
            title="No service history yet"
            description="Log oil changes, alignments, and inspections. History stays private until you share it."
          />
        ) : (
          <ul className="divide-y divide-white/[0.06] rounded-xl border border-white/10 overflow-hidden">
            {items.slice(0, 12).map((item) => (
              <li key={item.id} className="px-4 py-3 bg-black/20">
                <div className="flex justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-100 truncate">{item.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {item.category.replace(/_/g, ' ')}
                      {(item.document_count ?? 0) > 0 ? ' · Receipt attached' : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0 text-xs text-zinc-500">
                    <p>{item.service_date}</p>
                    {item.mileage != null ? <p className="tabular-nums">{item.mileage.toLocaleString()} mi</p> : null}
                  </div>
                </div>
                {expandedReceiptId === item.id || pendingReceiptId === item.id ? (
                  <MaintenanceReceiptUpload
                    maintenanceId={item.id}
                    receiptPath={item.receipt_path}
                    receiptMime={item.receipt_mime}
                    onUploaded={() => void refreshAfterReceipt()}
                    onDone={() => {
                      setExpandedReceiptId(null);
                      if (pendingReceiptId === item.id) setPendingReceiptId(null);
                    }}
                  />
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => setExpandedReceiptId(item.id)}
                  >
                    {(item.document_count ?? 0) > 0 ? 'Manage receipt' : 'Add receipt'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <form onSubmit={createRecord} className="rounded-xl border border-white/10 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-white">Log service</h3>
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-xs text-zinc-500">Describe in plain language</span>
          <PremiumBadge featureLabel="Smart maintenance entry" variant="compact" />
        </div>
        <PremiumLockedSection
          entitled={hasPremiumAi || isPremium}
          capability="premium_ai"
          featureKey="maintenance_ai_entry"
          title="Smart maintenance entry"
          description="Describe a service in plain language and Bilt:Spec fills the form for you."
          helperText="Premium helps turn plain language into a structured service record."
          surface="maintenance_panel"
        >
          <textarea
            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white min-h-[72px]"
            value={nlText}
            onChange={(e) => setNlText(e.target.value)}
            placeholder="Oil change at 84,221 miles on 2026-08-01"
            aria-label="Natural language service description"
            disabled={!(hasPremiumAi || isPremium)}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            disabled={nlBusy || !nlText.trim() || !(hasPremiumAi || isPremium)}
            onClick={() => void parseNl()}
          >
            {nlBusy ? 'Parsing…' : 'Fill form from text'}
          </Button>
        </PremiumLockedSection>
        <label className="block text-xs text-zinc-500">
          Title
          <input
            className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Oil + filter"
            required
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs text-zinc-500">
            Date
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
              required
            />
          </label>
          <label className="block text-xs text-zinc-500">
            Mileage
            <input
              type="number"
              inputMode="numeric"
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              value={entryMileage}
              onChange={(e) => setEntryMileage(e.target.value)}
              placeholder="84221"
            />
          </label>
        </div>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? 'Saving…' : 'Save record'}
        </Button>
        {pendingReceiptId && !items.some((item) => item.id === pendingReceiptId) ? (
          <div className="rounded-lg border border-white/10 bg-black/20 p-3">
            <p className="text-xs text-zinc-400 mb-2">Attach a receipt to the record you just saved.</p>
            <MaintenanceReceiptUpload
              maintenanceId={pendingReceiptId}
              onUploaded={() => void refreshAfterReceipt()}
              onDone={() => setPendingReceiptId(null)}
            />
          </div>
        ) : null}
      </form>

      <section className="rounded-xl border border-white/10 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-white">Reminder</h3>
        <p className="text-xs text-zinc-500">
          Free reminders use one interval. Mileage and time together is included with Bilt:Spec Premium.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs text-zinc-500">
            Every (miles)
            <input
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              value={scheduleMiles}
              onChange={(e) => setScheduleMiles(e.target.value)}
            />
          </label>
          <label className="block text-xs text-zinc-500">
            Every (months)
            <input
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              value={scheduleMonths}
              onChange={(e) => setScheduleMonths(e.target.value)}
              placeholder={isPremium ? '6' : 'Premium'}
            />
          </label>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={() => void createSchedule()}>
          Add schedule
        </Button>
        <PremiumFeatureGate
          locked={scheduleGate}
          capability="premium_maintenance"
          title="Advanced maintenance schedules"
          description="Set mileage and time intervals together, and Bilt:Spec will keep track of what is coming up."
        />
      </section>

        <PremiumFeatureGate
          locked={aiGate}
          capability="premium_ai"
          title="Natural-language service entry"
          description="Describe a service in plain language and Bilt:Spec fills the form for you."
        />

      <section className="rounded-xl border border-white/10 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-white">Bilt:Spec Vehicle Record</h3>
          <PremiumBadge featureLabel="Bilt:Spec Vehicle Record" variant="compact" />
        </div>
        <p className="text-xs text-zinc-500">
          Export a printable ownership record with identity, mods, and service history.
        </p>
        <Button type="button" variant="secondary" size="sm" disabled={recordBusy} onClick={() => void downloadVehicleRecord()}>
          {recordBusy ? 'Building…' : 'Export Vehicle Record'}
        </Button>
        <PremiumFeatureGate
          locked={reportsGate}
          capability="premium_reports"
          title="Bilt:Spec Vehicle Record"
          description="Export a printable ownership record with identity, mods, and service history."
        />
      </section>
    </div>
  );
};

export default MaintenancePanel;
